/** /tag_name_aliases/ — cross-tenant Tag_Name_Alias catalog (list + CRUD).
 *
 * A tag name alias maps a tag NAME — or a name prefix — onto a canonical token
 * inside one category, so a condition written once as `reseller:~viator`
 * matches every spelling a tenant happens to carry. Production has 4,302
 * distinct reseller names, 60+ of them spellings of Viator.
 *
 * `tour_operator` is nullable: a null tenant is a system-wide alias, and a
 * tenant row shadows the system row with the same `(category_code, alias)` —
 * including shadowing it to `enabled: false`, which is how a tenant opts out.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). No optimistic-concurrency token — writes are
 * last-writer-wins.
 */
import { runBatch, type BatchResponse, type BatchSelection } from './batch';
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

/** How an alias is compared against a tag name. */
export type MatchMode = 'exact' | 'prefix';

/** Shortest accepted `prefix` alias — mirrors Tag_Name_Alias.MIN_PREFIX_LEN.
 *  A shorter one sweeps an arbitrary share of a category with thousands of
 *  names, so the backend rejects it too. */
export const MIN_PREFIX_LEN = 3;

export interface TagNameAlias {
  id: number;
  /** Owning tenant. Null for system-wide aliases. */
  tenant: TenantRef | null;
  /** Tag category this alias applies within, e.g. 'reseller'. Stored lowercased. */
  category_code: string;
  /** A tag name, or a name prefix. Matched case-insensitively, stored lowercased. */
  alias: string;
  /** The governed token this name normalises to, e.g. 'viator'. */
  canonical: string;
  match_mode: MatchMode;
  enabled: boolean;
  created_ts: string | null;
  updated_ts: string | null;
}

/** Create/update body. `category_code` + `alias` + `canonical` required on
 *  create; `tour_operator` null = system-wide. */
export interface TagNameAliasWrite {
  tour_operator?: number | null;
  category_code?: string;
  alias?: string;
  canonical?: string;
  match_mode?: MatchMode;
  enabled?: boolean;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  /** alias + canonical + category_code (icontains). */
  search?: string;
  /** One id or a comma list of ids (D1), or 'system' / 'null' for system-wide aliases (no tenant). */
  tour_operator_id?: TenantIdsParam;
  category_code?: string;
  canonical?: string;
  enabled?: boolean;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) {
    usp.set('tour_operator_id', String(params.tour_operator_id));
  }
  if (params.category_code) usp.set('category_code', params.category_code);
  if (params.canonical) usp.set('canonical', params.canonical);
  if (params.enabled !== undefined) usp.set('enabled', String(params.enabled));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<TagNameAlias>> {
  return apiGet<PaginatedResponse<TagNameAlias>>(`/tag_name_aliases/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<TagNameAlias> {
  return apiGet<TagNameAlias>(`/tag_name_aliases/${id}/`);
}

export function create(body: TagNameAliasWrite): Promise<TagNameAlias> {
  return apiPost<TagNameAlias>('/tag_name_aliases/', body);
}

export function update(id: number, body: TagNameAliasWrite): Promise<TagNameAlias> {
  return apiPatch<TagNameAlias>(`/tag_name_aliases/${id}/`, body);
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/tag_name_aliases/${id}/`);
}

/** Generic `/tag_name_aliases/batch/` delete dispatch (background task). */
export function batchDelete(
  selection: BatchSelection,
  filterQuery?: string,
): Promise<BatchResponse> {
  return runBatch('tag_name_aliases', 'delete', selection, undefined, filterQuery);
}
